<script setup lang="ts">
    import { IWidgetType, MergedWidget } from '@/js/services/api/widget/types';
    import { ref } from 'vue';
    import { MoveIcon, PencilIcon, SettingsIcon, TrashIcon } from '@lucide/vue';
    import WidgetFooter from './WidgetFooter.vue';
    import EchoPopover from '../EchoPopover.vue';
    import EchoButton from '../EchoButton.vue';
    import { __ } from '@/js/stores/expressions';
    import WidgetContent from './WidgetContent.vue';
    import IconButton from '../IconButton.vue';
    import { isTextWidget } from '@/js/utils/widgetUtils';
    import { useWidgetFormStore } from '@/js/stores/widgetForm';

    type Props = {
        widget: MergedWidget<IWidgetType>;
        dashboardId?: string;
        isPlaceholder?: boolean;
        isStatic: boolean;
    };

    const emit = defineEmits<{
        (e: 'editWidget'): void;
        (e: 'deleteWidget'): void;
        (e: 'updateComment', value: string): void;
    }>();

    const props = defineProps<Props>();

    const popover = ref<InstanceType<typeof EchoPopover> | null>(null);

    const handleEdit = () => {
        popover.value?.close();
        emit('editWidget');
    };

    const handleRemoveInvalidFilter = () => {
        useWidgetFormStore().removeInvalidLevelFilterForWidgetId = props.widget.id;
        emit('editWidget');
    };

    const handleDelete = () => {
        popover.value?.close();
        emit('deleteWidget');
    };
</script>

<template>
    <div class="grid-widget-root relative h-full border border-gray-lightest bg-white rounded-lg">
        <div class="h-full relative grid grid-rows-[auto_1fr_auto] grid-cols-[100%] min-h-0">
            <div v-if="!isStatic" class="Drag__pan absolute top-3 left-3 z-1 padding-2 hidden">
                <MoveIcon :aria-label="'widgetResize'" />
            </div>
            <div class="pb-4">
                <div
                    v-if="props.widget?.title || props.widget?.description"
                    class="w-full pt-8 pb-8 -mb-4 px-2 flex flex-col items-center justify-center"
                >
                    <h1
                        v-if="props.widget?.title"
                        :class="
                            isTextWidget(props.widget) ? 'grid-widget-title-text' : 'heading-md'
                        "
                        class="text-center"
                    >
                        {{ props.widget?.title }}
                    </h1>
                    <p
                        v-if="props.widget?.description"
                        :class="isTextWidget(props.widget) ? 'grid-widget-desc-text' : 'text-sm'"
                        class="text-center border-t border-gray-light pt-1 mt-1"
                    >
                        {{ props.widget.description }}
                    </p>
                </div>
            </div>
            <div class="relative h-full min-h-0 overflow-hidden group">
                <EchoPopover v-if="!isStatic" ref="popover" type="menu" position="bottom-end">
                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                        <div
                            :ref="anchor"
                            class="absolute right-2 bottom-2 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 group-focus-within:opacity-100"
                        >
                            <IconButton
                                size="size-6"
                                area-size="size-8"
                                :tooltip="__('widget-settings')"
                                v-bind="buttonAttrs"
                                @click="togglePopover"
                            >
                                <SettingsIcon />
                            </IconButton>
                        </div>
                    </template>

                    <template #popover>
                        <EchoButton variant="block" @click="handleEdit">
                            <template #icon-left><PencilIcon /></template>
                            {{ __('widget-settings') }}
                        </EchoButton>

                        <EchoButton variant="block" @click="handleDelete">
                            <template #icon-left><TrashIcon /></template>
                            {{ __('widget-delete') }}
                        </EchoButton>
                    </template>
                </EchoPopover>
                <div class="h-full @container">
                    <WidgetContent
                        v-if="props.widget"
                        :widget="props.widget"
                        :dashboard-id="props.dashboardId"
                        :can-edit="!isStatic"
                        @remove-invalid-filter="handleRemoveInvalidFilter"
                    />
                </div>
            </div>
            <div>
                <WidgetFooter
                    :comment="widget?.comment"
                    :filter-selection="
                        (widget as MergedWidget<'Graph'>)?.graphWidget?.filterSelection ??
                        (widget as MergedWidget<'Graph'>)?.graphWidgetWithoutData?.filterSelection
                    "
                    :is-static="isStatic"
                    @update-comment="$emit('updateComment', $event)"
                />
            </div>
        </div>
    </div>
</template>
